import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sparkles, Loader2, Plus, X, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useCanvasStore } from '@/stores/canvasStore';
import { toast } from 'sonner';

interface AnalyzeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SourceEntry {
  id: string;
  platform: string;
  account_handle: string;
  account_url?: string;
}

export function AnalyzeModal({ open, onOpenChange }: AnalyzeModalProps) {
  const [sources, setSources] = useState<SourceEntry[]>([
    { id: crypto.randomUUID(), platform: 'tiktok', account_handle: '' },
  ]);
  const [lookbackDays, setLookbackDays] = useState<string>('30');
  const [engagementThreshold, setEngagementThreshold] = useState<string>('4');
  const [vertical, setVertical] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { navigateToBoard } = useCanvasStore();

  const addSource = () => {
    if (sources.length >= 10) {
      toast.error('Maximum 10 accounts per analysis');
      return;
    }
    setSources(prev => [
      ...prev,
      { id: crypto.randomUUID(), platform: 'tiktok', account_handle: '' },
    ]);
  };

  const removeSource = (id: string) => {
    if (sources.length <= 1) return;
    setSources(prev => prev.filter(s => s.id !== id));
  };

  const updateSource = (id: string, field: keyof SourceEntry, value: string) => {
    setSources(prev =>
      prev.map(s => s.id === id ? { ...s, [field]: value } : s)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate at least one source has a handle
    const validSources = sources.filter(s => s.account_handle.trim());
    if (validSources.length === 0) {
      toast.error('Please enter at least one account handle');
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('apify-scrape', {
        body: {
          sources: validSources.map(s => ({
            platform: s.platform,
            account_handle: s.account_handle.trim().replace(/^@/, ''),
            account_url: s.account_url || undefined,
          })),
          lookback_days: parseInt(lookbackDays),
          engagement_threshold: parseFloat(engagementThreshold) / 100,
          vertical: vertical.trim() || undefined,
        },
      });

      if (error) throw error;

      toast.success(
        `Analysis started! Found ${data.creatives_found} top creatives from ${data.total_scraped} posts.`,
        { duration: 5000 }
      );

      // Navigate to the new analysis board
      if (data.board_id) {
        navigateToBoard(data.board_id);
      }

      handleClose();
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to start analysis. Please check your Apify configuration.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSources([{ id: crypto.randomUUID(), platform: 'tiktok', account_handle: '' }]);
      setLookbackDays('30');
      setEngagementThreshold('4');
      setVertical('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-500" />
            Analyze Top Creatives
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground -mt-1">
          Scrape top-performing posts from selected accounts. Results will appear on a new analysis board with Motion-style scoring.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Accounts */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Accounts to Analyze</Label>
            <div className="space-y-2">
              {sources.map((source, idx) => (
                <div key={source.id} className="flex items-center gap-2">
                  <Select
                    value={source.platform}
                    onValueChange={(val) => updateSource(source.id, 'platform', val)}
                  >
                    <SelectTrigger className="w-[120px] h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                    </SelectContent>
                  </Select>

                  <Input
                    placeholder={`@handle ${idx === 0 ? '(required)' : ''}`}
                    value={source.account_handle}
                    onChange={(e) => updateSource(source.id, 'account_handle', e.target.value)}
                    className="h-9 flex-1"
                    disabled={isLoading}
                  />

                  {sources.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSource(source.id)}
                      className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                      disabled={isLoading}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addSource}
              disabled={isLoading || sources.length >= 10}
              className="text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Account
            </Button>
          </div>

          {/* Settings row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Lookback Window</Label>
              <Select value={lookbackDays} onValueChange={setLookbackDays}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                Min Engagement
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="1"
                  max="20"
                  step="0.5"
                  value={engagementThreshold}
                  onChange={(e) => setEngagementThreshold(e.target.value)}
                  className="h-9 pr-6"
                  disabled={isLoading}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
              </div>
            </div>
          </div>

          {/* Vertical (optional) */}
          <div className="space-y-1.5">
            <Label className="text-sm">Vertical <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              placeholder="e.g. skincare, fitness, SaaS, DTC fashion"
              value={vertical}
              onChange={(e) => setVertical(e.target.value)}
              className="h-9"
              disabled={isLoading}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !sources.some(s => s.account_handle.trim())}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Scraping...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Start Analysis
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
